// The maker's side of the code: example boards for each fictional hero unit.
// Built from what the back office really has (Oct 2026): configurable boards of KPI, trend, distribution and
// work-queue tiles; registrations over time and units by status; support topics, AI resolution rate and
// satisfaction; knowledge recommendations; cases, returns and recalls by scope; orders, net revenue and
// abandoned checkouts. Cases by build lot assumes lot is a board dimension (confirm with product).
// Every number is illustrative and every name fictional. Never show the admin navigation or data model.
import type { Status } from '../content';
import type { HeroUnit } from '../heroUnits';

export type BoardKey = 'owners' | 'asks' | 'breaks' | 'earns';

export const BOARDS: { key: BoardKey; label: string; q: string; status: Status }[] = [
  { key: 'owners', label: 'Owners', q: 'Who has it', status: 'live' },
  { key: 'asks', label: 'Questions', q: 'What they ask', status: 'live' },
  { key: 'breaks', label: 'Cases', q: 'What goes wrong', status: 'live' },
  { key: 'earns', label: 'Revenue', q: 'What it earns', status: 'pilot' },
];

export type Topic = { t: string; n: number; trend: 'up' | 'flat' | 'down'; note: string };
export type Line = { t: string; v: string; sub?: string };

export type BoardData = {
  line: string; // product line the board is scoped to
  owners: {
    registered: string;
    month: string;
    newOwners: string;
    secondOwners: string;
    weeks: number[]; // registrations per week, 12 weeks
    status: [string, number][]; // units by status, shares that add to 100
    pending: string;
  };
  asks: {
    resolved: string;
    csat: string;
    topics: Topic[];
    gap: { n: number; t: string };
  };
  breaks: {
    open: string;
    returns: string;
    inRepair: string;
    fault: string;
    lot: string;
    cases: number;
    fromLot: number;
    serials: string;
    units: number;
    to: string;
  };
  earns: {
    revenue: string;
    orders: string;
    abandoned: string;
    months: number[]; // 6 months
    top: Line[];
  };
};

const W = (base: number, k: number) => Array.from({ length: 12 }, (_, i) => Math.round(base * (0.72 + 0.05 * i + 0.09 * Math.sin(i * 1.7 + k))));

export const BOARD_DATA: Record<HeroUnit['key'], BoardData> = {
  heatpump: {
    line: 'Halvard AW-9',
    owners: {
      registered: '3,846',
      month: '+212',
      newOwners: '198',
      secondOwners: '146',
      weeks: W(52, 1),
      status: [['With installer', 6], ['In use', 84], ['Second owner', 4], ['In repair', 1], ['Taken back', 5]],
      pending: '9 commissioning records to check',
    },
    asks: {
      resolved: '82%',
      csat: '4.7',
      topics: [
        { t: 'Flow temperature and radiators', n: 412, trend: 'flat', note: 'Answered from the installation manual' },
        { t: 'E21, fan stopped', n: 38, trend: 'up', note: 'Up from 9 last month' },
        { t: 'Noise at night', n: 64, trend: 'down', note: 'Quiet mode tip working' },
        { t: 'Annual service booking', n: 121, trend: 'up', note: 'Handed to installers' },
      ],
      gap: { n: 12, t: 'questions about frost protection had no answer in your documents' },
    },
    breaks: {
      open: '14',
      returns: '3',
      inRepair: '41',
      fault: 'E21, fan stopped',
      lot: 'L2602',
      cases: 9,
      fromLot: 7,
      serials: 'KAW9-26-03950 to 04300',
      units: 351,
      to: 'Homeowners and their installers',
    },
    earns: {
      revenue: '£18,420',
      orders: '312',
      abandoned: '23',
      months: [9.2, 10.1, 11.8, 12.6, 15.9, 18.4],
      top: [
        { t: 'System filter cartridge', v: '£4,810', sub: '260 sold' },
        { t: 'Extended cover, years 3 to 7', v: '£6,180', sub: '42 sold' },
        { t: 'Fan motor assembly', v: '£1,640', sub: '19 sold' },
      ],
    },
  },
  mower: {
    line: 'Moorcroft RM-42',
    owners: {
      registered: '1,274',
      month: '+168',
      newOwners: '151',
      secondOwners: '61',
      weeks: W(24, 2),
      status: [['With dealer', 11], ['In use', 78], ['Second owner', 6], ['In repair', 2], ['Taken back', 3]],
      pending: '6 pre-delivery inspections to check',
    },
    asks: {
      resolved: '79%',
      csat: '4.6',
      topics: [
        { t: 'Will not start after winter', n: 186, trend: 'up', note: 'Seat switch and battery steps' },
        { t: 'Cutting height for wet grass', n: 94, trend: 'flat', note: "Answered from the owner's manual" },
        { t: 'Blade engagement warning', n: 27, trend: 'up', note: 'Up from 6 last month' },
        { t: 'Spring service booking', n: 143, trend: 'up', note: 'Handed to dealers' },
      ],
      gap: { n: 8, t: 'questions about towing a trailer had no answer in your documents' },
    },
    breaks: {
      open: '11',
      returns: '2',
      inRepair: '22',
      fault: 'Blade engagement warning',
      lot: 'L2605',
      cases: 8,
      fromLot: 6,
      serials: 'MRM42-26-02980 to 03240',
      units: 260,
      to: 'Owners and their dealers',
    },
    earns: {
      revenue: '£14,960',
      orders: '388',
      abandoned: '31',
      months: [4.1, 6.3, 11.2, 14.9, 12.7, 14.96],
      top: [
        { t: 'Blades and spring service kit', v: '£6,420', sub: '138 sold' },
        { t: 'Mulching kit', v: '£3,740', sub: '34 sold' },
        { t: 'Starter battery', v: '£2,050', sub: '38 sold' },
      ],
    },
  },
  oven: {
    line: 'Fornell CX-10',
    owners: {
      registered: '612',
      month: '+41',
      newOwners: '38',
      secondOwners: '23',
      weeks: W(10, 3),
      status: [['With service partner', 5], ['In use', 86], ['Second owner', 4], ['In repair', 3], ['Taken back', 2]],
      pending: '3 electrical safety checks to review',
    },
    asks: {
      resolved: '74%',
      csat: '4.5',
      topics: [
        { t: 'Descaling in hard-water areas', n: 158, trend: 'up', note: 'Answered from the service manual' },
        { t: 'No steam, light flashing', n: 22, trend: 'up', note: 'Up from 5 last month' },
        { t: 'Core probe readings', n: 71, trend: 'flat', note: 'Calibration steps' },
        { t: 'Cleaning cycle times', n: 66, trend: 'down', note: 'New quick guide working' },
      ],
      gap: { n: 6, t: 'questions about overnight slow cooking had no answer in your documents' },
    },
    breaks: {
      open: '7',
      returns: '1',
      inRepair: '18',
      fault: 'Steam generator self-test',
      lot: 'L2604',
      cases: 6,
      fromLot: 5,
      serials: 'FCX10-26-00140 to 00260',
      units: 120,
      to: 'Kitchens and their service partners',
    },
    earns: {
      revenue: '£21,300',
      orders: '204',
      abandoned: '9',
      months: [14.2, 15.8, 16.1, 18.9, 19.7, 21.3],
      top: [
        { t: 'Descaler cartridges', v: '£8,960', sub: '280 sold' },
        { t: 'Planned maintenance plan', v: '£7,200', sub: '16 sold' },
        { t: 'Steam generator element', v: '£2,220', sub: '15 sold' },
      ],
    },
  },
  ebike: {
    line: 'Velora T8',
    owners: {
      registered: '5,318',
      month: '+406',
      newOwners: '371',
      secondOwners: '388',
      weeks: W(96, 4),
      status: [['With dealer', 7], ['In use', 79], ['Second owner', 9], ['In repair', 2], ['Taken back', 3]],
      pending: '14 handovers waiting for the rider',
    },
    asks: {
      resolved: '86%',
      csat: '4.6',
      topics: [
        { t: 'Range in cold weather', n: 534, trend: 'up', note: 'Answered from the battery manual' },
        { t: 'Battery not charging fully', n: 47, trend: 'up', note: 'Up from 12 last month' },
        { t: 'Pairing the display', n: 212, trend: 'down', note: 'New setup video working' },
        { t: 'Annual service booking', n: 301, trend: 'up', note: 'Handed to dealers' },
      ],
      gap: { n: 15, t: 'questions about carrying it on a car rack had no answer in your documents' },
    },
    breaks: {
      open: '19',
      returns: '6',
      inRepair: '96',
      fault: 'Battery not charging fully',
      lot: 'L2607',
      cases: 12,
      fromLot: 10,
      serials: 'VT8-26-17200 to 17980',
      units: 780,
      to: 'Riders and their dealers',
    },
    earns: {
      revenue: '£26,840',
      orders: '1,126',
      abandoned: '64',
      months: [17.4, 18.2, 21.6, 24.9, 25.3, 26.84],
      top: [
        { t: 'Brake pads and chain', v: '£9,870', sub: '235 sold' },
        { t: 'Battery health check', v: '£5,950', sub: '170 booked' },
        { t: 'Rear rack and panniers', v: '£4,180', sub: '58 sold' },
      ],
    },
  },
  lift: {
    line: 'Talbrook TX12',
    owners: {
      registered: '2,184',
      month: '+96',
      newOwners: '64',
      secondOwners: '118',
      weeks: W(22, 7),
      status: [['With service partner', 5], ['On hire', 68], ['In the depot', 17], ['In repair', 4], ['Second owner', 6]],
      pending: '9 examination reports to check',
    },
    asks: {
      resolved: '81%',
      csat: '4.5',
      topics: [
        { t: 'Emergency lowering', n: 164, trend: 'up', note: 'Answered from the operator manual' },
        { t: 'Pre-use check steps', n: 412, trend: 'flat', note: 'Check form on the page' },
        { t: 'Pothole guard not deploying', n: 31, trend: 'up', note: 'Up from 8 last month' },
        { t: 'Thorough examination booking', n: 226, trend: 'up', note: 'Handed to service partners' },
      ],
      gap: { n: 11, t: 'questions about working on a slope had no answer in your documents' },
    },
    breaks: {
      open: '14',
      returns: '1',
      inRepair: '27',
      fault: 'Pothole guard not deploying',
      lot: 'L2311',
      cases: 10,
      fromLot: 8,
      serials: 'TB-TX12-2023-1100 to 1300',
      units: 200,
      to: 'Hire fleets and their service partners',
    },
    earns: {
      revenue: '£18,420',
      orders: '214',
      abandoned: '12',
      months: [12.8, 13.6, 14.9, 15.2, 16.8, 18.42],
      top: [
        { t: 'Hydraulic service kit', v: '£7,960', sub: '92 sold' },
        { t: 'Pothole guard set', v: '£4,380', sub: '34 sold' },
        { t: 'Platform gate', v: '£2,640', sub: '11 sold' },
      ],
    },
  },
  battery: {
    line: 'Aurel H13',
    owners: {
      registered: '4,206',
      month: '+318',
      newOwners: '289',
      secondOwners: '74',
      weeks: W(70, 5),
      status: [['With installer', 8], ['In use', 86], ['Second owner', 3], ['In repair', 1], ['Taken back', 2]],
      pending: '11 commissioning records to check',
    },
    asks: {
      resolved: '84%',
      csat: '4.8',
      topics: [
        { t: 'Backup in a power cut', n: 388, trend: 'up', note: 'Reserve setting steps' },
        { t: 'Charging on a cheaper tariff', n: 512, trend: 'flat', note: 'Answered from the owner guide' },
        { t: 'Gateway relay test fails', n: 29, trend: 'up', note: 'Up from 7 last month' },
        { t: 'Annual health check booking', n: 164, trend: 'up', note: 'Handed to installers' },
      ],
      gap: { n: 10, t: 'questions about adding a second battery had no answer in your documents' },
    },
    breaks: {
      open: '12',
      returns: '2',
      inRepair: '18',
      fault: 'Gateway relay test fails',
      lot: 'L2603',
      cases: 8,
      fromLot: 7,
      serials: 'AH13-26-06900 to 07300',
      units: 400,
      to: 'Homeowners and their installers',
    },
    earns: {
      revenue: '£24,960',
      orders: '388',
      abandoned: '31',
      months: [11.4, 13.2, 15.0, 17.6, 21.8, 24.96],
      top: [
        { t: 'Backup gateway upgrade', v: '£9,800', sub: '49 sold' },
        { t: 'Extended cover, years 10 to 15', v: '£8,640', sub: '36 sold' },
        { t: 'Cooling fan filter', v: '£1,420', sub: '142 sold' },
      ],
    },
  },
  robotmower: {
    line: 'Verdo R7',
    owners: {
      registered: '8,942',
      month: '+1,204',
      newOwners: '1,122',
      secondOwners: '212',
      weeks: W(190, 6),
      status: [['With dealer', 9], ['In use', 82], ['Second owner', 4], ['In repair', 2], ['Taken back', 3]],
      pending: '23 installations waiting for the owner',
    },
    asks: {
      resolved: '88%',
      csat: '4.6',
      topics: [
        { t: 'Lost signal near hedges', n: 846, trend: 'up', note: 'Lawn map steps working' },
        { t: 'Mowing schedule and height', n: 1210, trend: 'flat', note: "Answered from the owner's guide" },
        { t: 'Stalls on slopes', n: 41, trend: 'up', note: 'Up from 11 last month' },
        { t: 'Winter service booking', n: 392, trend: 'up', note: 'Handed to dealers' },
      ],
      gap: { n: 19, t: 'questions about mowing a second lawn had no answer in your documents' },
    },
    breaks: {
      open: '21',
      returns: '9',
      inRepair: '64',
      fault: 'Stalls on slopes',
      lot: 'L2604',
      cases: 14,
      fromLot: 11,
      serials: 'VR7-26-21800 to 22900',
      units: 1100,
      to: 'Owners and their dealers',
    },
    earns: {
      revenue: '£38,210',
      orders: '2,416',
      abandoned: '118',
      months: [12.8, 19.6, 27.4, 34.2, 36.9, 38.21],
      top: [
        { t: 'Replacement blade set', v: '£14,620', sub: '1,720 sold' },
        { t: 'Garage and rain cover', v: '£12,480', sub: '208 sold' },
        { t: 'Extended cover, years 3 to 5', v: '£6,300', sub: '90 sold' },
      ],
    },
  },
  icemaker: {
    line: 'Nordis IM-150',
    owners: {
      registered: '1,862',
      month: '+96',
      newOwners: '88',
      secondOwners: '41',
      weeks: W(22, 7),
      status: [['With service partner', 5], ['In use', 86], ['Second owner', 5], ['In repair', 2], ['Taken back', 2]],
      pending: '4 installation sign-offs to check',
    },
    asks: {
      resolved: '81%',
      csat: '4.5',
      topics: [
        { t: 'Cloudy or small ice', n: 214, trend: 'up', note: 'Filter and clean steps' },
        { t: 'Cleaning and sanitising', n: 306, trend: 'flat', note: 'Answered from the service manual' },
        { t: 'Slow fill', n: 33, trend: 'up', note: 'Up from 8 last month' },
        { t: 'Six-month clean booking', n: 188, trend: 'up', note: 'Handed to service partners' },
      ],
      gap: { n: 7, t: 'questions about hard-water areas had no answer in your documents' },
    },
    breaks: {
      open: '9',
      returns: '1',
      inRepair: '14',
      fault: 'Slow fill',
      lot: 'L2602',
      cases: 7,
      fromLot: 6,
      serials: 'NIM150-26-01200 to 01560',
      units: 360,
      to: 'Bars and their service partners',
    },
    earns: {
      revenue: '£16,740',
      orders: '612',
      abandoned: '14',
      months: [9.8, 11.2, 12.6, 13.9, 15.1, 16.74],
      top: [
        { t: 'Water filter cartridge', v: '£7,920', sub: '660 sold' },
        { t: 'Planned maintenance plan', v: '£5,600', sub: '14 sold' },
        { t: 'Ice scoop and holder', v: '£980', sub: '140 sold' },
      ],
    },
  },
  coffee: {
    line: 'Corvino E2',
    owners: {
      registered: '2,734',
      month: '+188',
      newOwners: '164',
      secondOwners: '97',
      weeks: W(36, 8),
      status: [['With service partner', 6], ['In use', 84], ['Second owner', 6], ['In repair', 2], ['Taken back', 2]],
      pending: '7 installation sign-offs to check',
    },
    asks: {
      resolved: '83%',
      csat: '4.7',
      topics: [
        { t: 'Dialling in the grind', n: 618, trend: 'flat', note: 'Answered from the barista guide' },
        { t: 'Steam wand drips', n: 52, trend: 'up', note: 'Up from 14 last month' },
        { t: 'Daily cleaning routine', n: 402, trend: 'down', note: 'New cleaning video working' },
        { t: 'Six-month service booking', n: 236, trend: 'up', note: 'Handed to service partners' },
      ],
      gap: { n: 11, t: 'questions about oat milk texturing had no answer in your documents' },
    },
    breaks: {
      open: '13',
      returns: '2',
      inRepair: '26',
      fault: 'Steam wand drips',
      lot: 'L2605',
      cases: 11,
      fromLot: 9,
      serials: 'CE2-26-05100 to 05700',
      units: 600,
      to: 'Cafés and their service partners',
    },
    earns: {
      revenue: '£22,380',
      orders: '1,488',
      abandoned: '42',
      months: [13.6, 15.4, 17.0, 18.8, 20.5, 22.38],
      top: [
        { t: 'Water filter and gasket kit', v: '£9,240', sub: '770 sold' },
        { t: 'Planned maintenance plan', v: '£8,400', sub: '28 sold' },
        { t: 'Group head cleaner', v: '£2,160', sub: '720 sold' },
      ],
    },
  },
  hottub: {
    line: 'Calder S6',
    owners: {
      registered: '1,516',
      month: '+142',
      newOwners: '131',
      secondOwners: '58',
      weeks: W(28, 9),
      status: [['With dealer', 7], ['In use', 84], ['Second owner', 5], ['In repair', 2], ['Taken back', 2]],
      pending: '8 handovers waiting for the owner',
    },
    asks: {
      resolved: '85%',
      csat: '4.7',
      topics: [
        { t: 'Cloudy water after use', n: 486, trend: 'up', note: 'Shock and filter steps' },
        { t: 'Water chemistry and chlorine', n: 702, trend: 'flat', note: "Answered from the owner's guide" },
        { t: 'Heater shows FL1', n: 37, trend: 'up', note: 'Up from 9 last month' },
        { t: 'Annual service booking', n: 208, trend: 'up', note: 'Handed to dealers' },
      ],
      gap: { n: 9, t: 'questions about winterising had no answer in your documents' },
    },
    breaks: {
      open: '10',
      returns: '1',
      inRepair: '19',
      fault: 'Heater shows FL1',
      lot: 'L2604',
      cases: 9,
      fromLot: 7,
      serials: 'CS6-26-02800 to 03200',
      units: 400,
      to: 'Owners and their dealers',
    },
    earns: {
      revenue: '£31,560',
      orders: '1,034',
      abandoned: '52',
      months: [18.2, 21.6, 24.0, 26.8, 29.4, 31.56],
      top: [
        { t: 'Filter cartridge pair', v: '£10,380', sub: '346 sold' },
        { t: 'Cover lifter', v: '£9,520', sub: '56 sold' },
        { t: 'Water care starter kit', v: '£4,860', sub: '270 sold' },
      ],
    },
  },
};

export const boardFor = (k: string) => BOARD_DATA[k as HeroUnit['key']] ?? BOARD_DATA.heatpump;
